import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import TurnstileWidget from "../../features/auth/components/TurnstileWidget";
import {
  ENTRY_VERIFICATION_HOSTNAME,
  fetchEntryVerificationStatus,
  isEntryVerificationHost,
  isEntryVerificationEnabled,
  verifyEntryToken,
} from "./entryVerification";
import "./EntryVerificationGate.css";

const ENTRY_SESSION_RECHECK_MS = 5 * 60 * 1000;

function resolveGateError(error) {
  const code = String(error?.message || "").toLowerCase();
  if (code.includes("unavailable") || Number(error?.status) >= 500) {
    return "Verification is temporarily unavailable. Please retry in a moment.";
  }
  if (code.includes("expired") || code.includes("timeout-or-duplicate")) {
    return "That verification expired. Please try again.";
  }
  return "We could not verify this visit. Please try again.";
}

export default function EntryVerificationGate({ children, hostname }) {
  const location = useLocation();
  const currentHostname =
    hostname || (typeof window !== "undefined" ? window.location.hostname : "");
  const requiresGate =
    isEntryVerificationHost(currentHostname) && isEntryVerificationEnabled();
  const [status, setStatus] = useState(requiresGate ? "checking" : "granted");
  const [challengeOpen, setChallengeOpen] = useState(false);
  const [error, setError] = useState("");
  const [resetKey, setResetKey] = useState(0);
  const mountedRef = useRef(true);
  const verificationInFlightRef = useRef(false);
  const requestGenerationRef = useRef(0);
  const sessionCheckGenerationRef = useRef(0);
  const hasGrantedSessionRef = useRef(false);
  const locationKey = `${location.pathname}${location.search}${location.hash}`;
  const previousLocationKeyRef = useRef(locationKey);

  const siteKey = useMemo(
    () => String(process.env.REACT_APP_TURNSTILE_SITE_KEY || "").trim(),
    [],
  );

  useEffect(() => {
    mountedRef.current = true;
    if (!requiresGate) {
      setStatus("granted");
      return () => {
        mountedRef.current = false;
      };
    }

    let active = true;
    const checkSession = (initial = false) => {
      const checkGeneration = ++sessionCheckGenerationRef.current;
      if (initial) {
        setStatus("checking");
        setError("");
      }
      fetchEntryVerificationStatus()
        .then((verified) => {
          if (!active || !mountedRef.current || checkGeneration !== sessionCheckGenerationRef.current) return;
          if (verified) {
            hasGrantedSessionRef.current = true;
            if (!verificationInFlightRef.current) {
              setStatus("granted");
              setChallengeOpen(false);
            }
            return;
          }
          if (verificationInFlightRef.current) return;
          setStatus(siteKey ? "idle" : "error");
          setChallengeOpen(Boolean(siteKey));
          if (!siteKey) setError("Verification is not configured for this app host yet.");
        })
        .catch((verificationError) => {
          if (!active || !mountedRef.current || checkGeneration !== sessionCheckGenerationRef.current) return;
          if (verificationInFlightRef.current) return;
          setStatus("error");
          setError(resolveGateError(verificationError));
        });
    };

    checkSession(true);
    const recheckTimer = window.setInterval(
      () => checkSession(false),
      ENTRY_SESSION_RECHECK_MS,
    );

    return () => {
      active = false;
      sessionCheckGenerationRef.current += 1;
      window.clearInterval(recheckTimer);
      mountedRef.current = false;
    };
  }, [requiresGate, siteKey]);

  useEffect(() => {
    if (!requiresGate) return;
    if (previousLocationKeyRef.current === locationKey) return;
    previousLocationKeyRef.current = locationKey;
    requestGenerationRef.current += 1;
    const wasVerifying = verificationInFlightRef.current || status === "verifying";
    setChallengeOpen(false);
    verificationInFlightRef.current = false;
    setResetKey((key) => key + 1);
    if (wasVerifying) {
      setStatus(siteKey ? "idle" : "error");
      setError(siteKey ? "Verification was interrupted. Try again." : "Verification is not configured for this app host yet.");
      setChallengeOpen(Boolean(siteKey));
    }
  }, [locationKey, requiresGate, siteKey, status]);

  if (!requiresGate) return children;

  const openChallenge = () => {
    if (!siteKey) {
      setStatus("error");
      setError("Verification is not configured for this app host yet.");
      return;
    }
    setError("");
    setChallengeOpen(true);
    setResetKey((key) => key + 1);
  };

  const cancelChallenge = () => {
    requestGenerationRef.current += 1;
    verificationInFlightRef.current = false;
    setChallengeOpen(false);
    setResetKey((key) => key + 1);
    setStatus("error");
    setError("Verification cancelled. Please try again.");
  };

  const handleToken = async (token) => {
    if (!token || verificationInFlightRef.current) {
      if (!token) {
        setError("That verification expired. Please try again.");
        setResetKey((key) => key + 1);
      }
      return;
    }

    sessionCheckGenerationRef.current += 1;
    verificationInFlightRef.current = true;
    const requestGeneration = requestGenerationRef.current;
    setStatus("verifying");
    setError("");
    try {
      // The entry token exists only in this call. It is never placed in
      // storage or forwarded to Supabase auth/contact actions.
      await verifyEntryToken(token);
      if (!mountedRef.current || requestGeneration !== requestGenerationRef.current) return;
      sessionCheckGenerationRef.current += 1;
      hasGrantedSessionRef.current = true;
      setChallengeOpen(false);
      setStatus("granted");
    } catch (verificationError) {
      if (!mountedRef.current || requestGeneration !== requestGenerationRef.current) return;
      setStatus("idle");
      setError(resolveGateError(verificationError));
      setResetKey((key) => key + 1);
    } finally {
      if (requestGeneration === requestGenerationRef.current) {
        verificationInFlightRef.current = false;
      }
    }
  };

  const title = status === "checking" ? "Checking your access" : "A quick check before you enter";

  const gate = (
    <main
      className={`entry-verification${hasGrantedSessionRef.current ? " entry-verification--overlay" : ""}`}
      data-testid="entry-verification-gate"
    >
      <div className="entry-verification__texture" aria-hidden="true" />
      <section className="entry-verification__panel" aria-labelledby="entry-verification-title">
        <div className="entry-verification__mark" aria-hidden="true">R</div>
        <p className="entry-verification__eyebrow">Refanno / app</p>
        <h1 id="entry-verification-title" className="entry-verification__title">{title}</h1>
        <p className="entry-verification__copy">
          Checking your browser. You will continue automatically.
        </p>

        {status === "checking" || status === "verifying" ? (
          <p className="entry-verification__status" role="status">
            {status === "verifying" ? "Confirming verification..." : "Checking your access..."}
          </p>
        ) : null}

        {error ? <p className="entry-verification__error" role="alert">{error}</p> : null}

        {!challengeOpen && status === "error" ? (
          <button
            type="button"
            className="entry-verification__button"
            onClick={openChallenge}
            disabled={!siteKey}
          >
            Try verification again
          </button>
        ) : null}

        {challengeOpen ? (
          <div className="entry-verification__challenge" role="dialog" aria-modal="true" aria-label="Verify app access">
            <p className="entry-verification__challenge-title">Complete the Cloudflare check</p>
            <TurnstileWidget
              key={resetKey}
              action="refanno_app_entry"
              execution="execute"
              appearance="interaction-only"
              onToken={handleToken}
              onError={(verificationError) => {
                setStatus("idle");
                setError(resolveGateError(verificationError));
                setResetKey((key) => key + 1);
              }}
              showInlineError={false}
            />
            <button type="button" className="entry-verification__cancel" onClick={cancelChallenge} autoFocus>
              Cancel
            </button>
          </div>
        ) : null}

        <span className="sr-only">Current host: {ENTRY_VERIFICATION_HOSTNAME}</span>
      </section>
    </main>
  );

  if (!hasGrantedSessionRef.current) return gate;

  const appShell = status === "granted" ? (
    <div className="entry-verification__app-shell">
      <div className="entry-verification__app-content">{children}</div>
    </div>
  ) : (
    <div className="entry-verification__app-shell">
      <div className="entry-verification__app-content" aria-hidden="true" inert="">
        {children}
      </div>
      {gate}
    </div>
  );

  return appShell;
}
